import React from 'react';
import { Box } from '@mui/material';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  getNodesBounds,
  getOutgoers,
  getIncomers,
  NodeDragHandler,
  NodeMouseHandler,
  Edge as EdgeType,
  Node as NodeType,
  XYPosition,
  ControlButton,
  useReactFlow,
} from 'reactflow';
import {
  edgeTypes,
  nodeTypes,
  DUMMY_NODE,
  NODES_CONTAINER_MARGIN,
  NODE_MIN_HEIGHT,
  getNodeRect,
  isDraggingNodeOutOfBounds,
  sortOutNodesByVerticalPosition,
  sparkBaseEdge,
  DURATION_FOR_VIEW,
  MIN_ZOOM,
  NODE_DRAG_THRESHOLD,
  REACT_FLOW_BOUNDS,
  rePositionNodes,
} from './hooks';
import type { SparkButtonEdgeType } from './types';
import 'reactflow/dist/style.css';
import './style.css';
import { useSparkBuilderStore } from 'stores';

type SparkBuilderCanvasProps = {
  onNodeClickHandle: NodeMouseHandler;
  onNodeDropHandler: (
    node: NodeType,
    nodes: NodeType[],
    isDraggedAtLeastMinimumDistance: boolean,
    isDragSuccess: boolean
  ) => Promise<any>;
  onEdgeClickHandle: (
    event: React.MouseEvent<Element, MouseEvent>,
    node: EdgeType<any>,
    nodes: NodeType[]
  ) => void;
  sparkSampleEdge: SparkButtonEdgeType;
};

const SparkBuilderCanvas = ({
  onNodeClickHandle,
  onNodeDropHandler,
  onEdgeClickHandle,
  sparkSampleEdge,
}: SparkBuilderCanvasProps) => {
  const { fitBounds } = useReactFlow();
  const useReactFlowElements = useSparkBuilderStore(
    (store) => store.canvasActions.useReactFlowElements
  );
  const updateReactFlowElements = useReactFlowElements();

  const [nodes, setNodes, onNodesChange] = useNodesState<NodeType>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<EdgeType>([]);
  const [initialPosition, setInitialPosition] = React.useState<XYPosition>({
    x: 0,
    y: 0,
  });

  const [isValidDragForDropping, setIsValidDragForDropping] =
    React.useState(false);
  const [isNodeDraggedMinDistance, setIsNodeDraggedMinDistance] =
    React.useState(false);

  const onNodeDragStart: NodeDragHandler = (_, node: NodeType) => {
    if (!node || !node.id) {
      updateReactFlowElements();
      return;
    }
    setInitialPosition(node?.position);

    setNodes((pre) => [
      ...pre,
      {
        ...node,
        id: DUMMY_NODE,
      },
    ]);
    const filteredEdges = edges.filter(
      (edge) => edge?.source === node.id || edge?.target === node.id
    );
    const edgesWithoutDraggingNode = edges.filter(
      (edge) => edge?.source !== node.id && edge?.target !== node.id
    );
    const newEdgesConnectingDummy = [
      ...edgesWithoutDraggingNode,
      ...filteredEdges.map((mappedEdge) => {
        if (mappedEdge?.source === node.id) {
          return {
            ...mappedEdge,
            source: DUMMY_NODE,
          };
        }
        if (mappedEdge?.target === node.id) {
          return {
            ...mappedEdge,
            target: DUMMY_NODE,
          };
        }
        return mappedEdge;
      }),
    ];
    setEdges(newEdgesConnectingDummy);
  };

  const onNodeDrag: NodeDragHandler = (_, node) => {
    if (!node || !node.id) {
      updateReactFlowElements();
      return;
    }
    const centerY = node.position.y + (node?.height || NODE_MIN_HEIGHT) / 2;

    const currentNodeEdges = edges.filter(
      (edge) => edge?.source === DUMMY_NODE || edge?.target === DUMMY_NODE
    );
    const currentNodeSourceEdge = currentNodeEdges.find(
      (edge) => edge?.target === DUMMY_NODE
    );
    const currentNodeTargetEdge = currentNodeEdges.find(
      (edge) => edge?.source === DUMMY_NODE
    );
    const currentNodeSourceId = currentNodeSourceEdge?.source;
    const currentNodeTargetId = currentNodeTargetEdge?.target;
    const targetNodeData = nodes.find((n) => n.id === currentNodeTargetId);
    const sourceNodeData = nodes.find((n) => n.id === currentNodeSourceId);
    if (!targetNodeData || !sourceNodeData) {
      setIsValidDragForDropping(false);
      return;
    }
    const validDraggedUpwards =
      centerY <
      sourceNodeData?.position?.y + (sourceNodeData?.height || NODE_MIN_HEIGHT);
    const validDraggedDownwards =
      centerY >
      targetNodeData?.position?.y + (targetNodeData?.height || NODE_MIN_HEIGHT);
    // @ts-ignore
    const firstNodeOfChart = nodes.find((n) => n.data.isFirst);
    // @ts-ignore
    const lastNodeOfChart = nodes.find((n) => n.data.isLast);
    if (!firstNodeOfChart || !lastNodeOfChart) {
      setIsValidDragForDropping(false);
      return;
    }
    setIsNodeDraggedMinDistance(validDraggedDownwards || validDraggedUpwards);
    const draggedOutOfChart =
      centerY <
        firstNodeOfChart.position.y +
          (firstNodeOfChart.height || NODE_MIN_HEIGHT) ||
      centerY >
        lastNodeOfChart.position.y +
          (lastNodeOfChart.height || NODE_MIN_HEIGHT);

    const nodesBoundWhileDragging = getNodesBounds(
      [...nodes].filter((filterNode) => filterNode.id !== node.id)
    );
    const currentNodeBound = getNodeRect(node);

    const isCurrentlyOnAnyNode = [...nodes]
      .filter(
        (filterNode) =>
          filterNode.id !== DUMMY_NODE &&
          filterNode.id &&
          filterNode.id !== node.id
      )
      .some((someNode) => {
        const nodeBound = getNodeRect(someNode);
        const overlapLeft = Math.max(currentNodeBound.left, nodeBound.left);
        const overlapTop = Math.max(currentNodeBound.top, nodeBound.top);
        const overlapRight = Math.min(currentNodeBound.right, nodeBound.right);
        const overlapBottom = Math.min(
          currentNodeBound.bottom,
          nodeBound.bottom
        );

        return overlapLeft < overlapRight && overlapTop < overlapBottom;
      });
    const isDraggedOutOfBounds = isDraggingNodeOutOfBounds(
      currentNodeBound,
      nodesBoundWhileDragging,
      NODES_CONTAINER_MARGIN
    );

    const isValidDrag =
      (validDraggedUpwards || validDraggedDownwards) &&
      !draggedOutOfChart &&
      !isCurrentlyOnAnyNode &&
      !isDraggedOutOfBounds;

    const sortedNodes = sortOutNodesByVerticalPosition(nodes);
    const nodeIndex: number = sortedNodes.findIndex((n) => n.id === node.id);

    const nearestNodes = [
      sortedNodes[nodeIndex - 1],
      sortedNodes[nodeIndex + 1],
    ].filter((filterNode) => filterNode);

    if (nearestNodes?.length > 1) {
      const previewEdgeId = `${nearestNodes[0]?.id}-${nearestNodes[1]?.id}`;
      const isValidEdge = edges?.find((ed) => ed?.id === previewEdgeId);
      if (isValidEdge && isValidDrag) {
        setEdges((preEdges: any) =>
          preEdges.map((edge: any) => {
            if (edge?.id === previewEdgeId) {
              return {
                ...edge,
                data: {
                  ...edge?.data,
                  isPreviewNode: true,
                },
              };
            } else
              return {
                ...edge,
                data: {
                  ...edge?.data,
                  isPreviewNode: false,
                },
              };
          })
        );
      } else {
        setEdges((preEdges: any) =>
          preEdges.map((edge: any) => {
            return {
              ...edge,
              data: {
                ...edge?.data,
                isPreviewNode: false,
              },
            };
          })
        );
      }
    }

    setIsValidDragForDropping(isValidDrag);
  };
  const onNodeDragStop: NodeDragHandler = async (_, node) => {
    if (!node || !node.id) {
      updateReactFlowElements();
      return;
    }
    const dummyNode = nodes.find((n) => n.id === DUMMY_NODE);
    const dummyIncoming = getIncomers(dummyNode as NodeType, nodes, edges);
    const dummyOutgoing = getOutgoers(dummyNode as NodeType, nodes, edges);
    if (isValidDragForDropping) {
      const sortedNodes = sortOutNodesByVerticalPosition(nodes);
      const nodeIndex = sortedNodes.findIndex((n) => n.id === node.id);
      const nearestNodes = [
        sortedNodes[nodeIndex - 1],
        sortedNodes[nodeIndex + 1],
      ].filter((filterNode) => filterNode);

      setNodes((nodes) => {
        const filteredNodes = nodes.filter(
          (filterNode) =>
            filterNode.id !== DUMMY_NODE && filterNode.id !== node.id
        );
        const nodeWithNewPosition = {
          ...node,
          position: {
            x: node.position.x,
            y: node.position.y,
          },
        };
        const newNodesAll = filteredNodes.map((mappedNode) => {
          return {
            ...mappedNode,
            position: {
              x: mappedNode.position.x,
              y: mappedNode.position.y,
            },
          };
        });

        return [...newNodesAll, nodeWithNewPosition];
      });

      setEdges((edgesPrev: any) => {
        const removeDummyEdges = edgesPrev.filter(
          (edge: any) =>
            edge.source !== DUMMY_NODE && edge.target !== DUMMY_NODE
        );
        const edgeIdToRemoveInNeared = `${nearestNodes[0].id}-${nearestNodes[1].id}`;

        const removedNearestNodeEdge = removeDummyEdges.filter(
          (edge: any) => edge.id !== edgeIdToRemoveInNeared
        );

        const newEdge = {
          ...sparkSampleEdge,
          source: dummyIncoming[0].id,
          target: dummyOutgoing[0].id,
          id: `${dummyIncoming[0].id}-${dummyOutgoing[0].id}`,
          ...sparkBaseEdge,
        };
        const draggedNodeNewEdges1 = {
          ...sparkSampleEdge,
          source: nearestNodes[0].id,
          target: node.id,
          id: `${nearestNodes[0].id}-${node.id}`,
          ...sparkBaseEdge,
        };
        const draggedNodeNewEdges2 = {
          ...sparkSampleEdge,
          source: node.id,
          target: nearestNodes[1].id,
          id: `${node.id}-${nearestNodes[1].id}`,
          ...sparkBaseEdge,
        };

        return [
          ...removedNearestNodeEdge,
          newEdge,
          draggedNodeNewEdges1,
          draggedNodeNewEdges2,
        ];
      });
      const sortedNodesForReposition = sortOutNodesByVerticalPosition(
        [...[...nodes].filter((filterNode) => filterNode.id !== DUMMY_NODE)],
        true
      );

      //Implementing New rePositionFunction for proper gap
      const updatedNodes = rePositionNodes(sortedNodesForReposition);
      // const updatedNodes = sortedNodesForReposition.map((mappedNode, index) => {
      //   const newY = baseY + index * DISTANCE;
      //   return {
      //     ...mappedNode,
      //     position: {
      //       x: 0,
      //       y: newY,
      //     },
      //   };
      // });
      setNodes(updatedNodes);
    } else {
      setNodes((nodesPre: any) => {
        const nodesWithoutDummy = nodesPre.filter(
          (filterNode: NodeType) =>
            filterNode.id !== DUMMY_NODE && filterNode.id !== node.id
        );
        const oldNode = nodesPre.find(
          (findNode: NodeType) => findNode.id === node.id
        );
        const newNode = {
          ...oldNode,
          position: {
            x: initialPosition?.x,
            y: initialPosition?.y,
          },
        };
        return [...nodesWithoutDummy, newNode];
      });
      setEdges((edges: any) => {
        const removeDummyEdges = edges.filter(
          (filterEdge: any) =>
            filterEdge.source !== DUMMY_NODE && filterEdge.target !== DUMMY_NODE
        );
        const newIncomingEdge = {
          ...sparkSampleEdge,
          source: dummyIncoming[0]?.id,
          target: node.id,
          id: `${dummyIncoming[0]?.id}-${node.id}`,
          ...sparkBaseEdge,
        };
        const newOutgoingEdge = {
          ...sparkSampleEdge,
          source: node.id,
          target: dummyOutgoing[0]?.id,
          id: `${node.id}-${dummyOutgoing[0]?.id}`,
          ...sparkBaseEdge,
        };
        return [...removeDummyEdges, newIncomingEdge, newOutgoingEdge];
      });
    }
    onNodeDropHandler(
      node,
      sortOutNodesByVerticalPosition(nodes).filter((n) => n.id !== DUMMY_NODE),
      isNodeDraggedMinDistance,
      isValidDragForDropping
    );

    setIsValidDragForDropping(false);
    setIsNodeDraggedMinDistance(false);
  };

  const onNodeClick: NodeMouseHandler = (evt, node) => {
    onNodeClickHandle(evt, node);
  };
  const onEdgeClick: (
    evt: React.MouseEvent<Element, MouseEvent>,
    edge: EdgeType
  ) => void = (evt, edge) => {
    onEdgeClickHandle(evt, edge, nodes);
  };

  return (
    <Box className='spark_chart_container nokey' sx={{ position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        edgeTypes={edgeTypes}
        nodeTypes={nodeTypes}
        nodeDragThreshold={NODE_DRAG_THRESHOLD}
        minZoom={MIN_ZOOM}
        panOnScroll
        disableKeyboardA11y
      >
        <Controls
          fitViewOptions={{
            duration: DURATION_FOR_VIEW,
          }}
        >
          <ControlButton onClick={() => fitBounds(REACT_FLOW_BOUNDS)}>
            ↑
          </ControlButton>
        </Controls>

        <Background />
      </ReactFlow>
    </Box>
  );
};

export default SparkBuilderCanvas;
